import CONTROLLERS from '@/features/operations/components/pages/MapView/components/LayersMenu/legends/controllers.svg'
import CONTROLLERS_C from '@/features/operations/components/pages/MapView/components/LayersMenu/legends/controllers_c.svg'
import WEATHER_STATIONS from '@/features/operations/components/pages/MapView/components/LayersMenu/legends/weather_stations.svg'
import SOIL_MOISTURE_SENSOR from '@/features/operations/components/pages/MapView/components/LayersMenu/legends/soil_moisture_sensor.svg'
import FLOW_SENSOR from '@/features/operations/components/pages/MapView/components/LayersMenu/legends/flow_sensor.svg'
import CUSTOM_MARKERS from '@/features/operations/components/pages/MapView/components/LayersMenu/legends/custom_markers.svg'
import INTERFACE_NAME from '@/features/operations/components/pages/MapView/components/LayersMenu/legends/interface_name.svg'
import SENSOR_STATION_ERROR from '@/features/operations/components/pages/MapView/components/LayersMenu/legends/sensor_stations_error.svg'
import SENSOR_STATION_NORMAL from '@/features/operations/components/pages/MapView/components/LayersMenu/legends/sensor_station_normal.svg'
import SENSOR_STATION_NOT_REACHABLE from '@/features/operations/components/pages/MapView/components/LayersMenu/legends/sensor_station_not_reachable.svg'
import VALVE_ON from '@/features/operations/components/pages/MapView/components/LayersMenu/legends/valve_on.svg'
import VALVE_OFF from '@/features/operations/components/pages/MapView/components/LayersMenu/legends/valve_off.svg'
import VALVE_ERROR from '@/features/operations/components/pages/MapView/components/LayersMenu/legends/valve_error.svg'
import VALVE_WAITING from '@/features/operations/components/pages/MapView/components/LayersMenu/legends/valve_waiting.svg'
import VALVE_OFF_LOADING from '@/features/operations/components/pages/MapView/components/LayersMenu/legends/valve_off_loading.svg'

import { MARKER, MARKER_TYPE } from '@/types'
import { CONTROLLER_MARKER_ICON } from '@/ultils/marker'

export enum ELegendName {
  CONTROLLERS = 'Controllers',
  WEATHER_STATIONS = 'Weather Stations',
  SOIL_MOISTURE_SENSOR = 'Soil Moisture Sensor',
  FLOW_SENSOR = 'Flow Sensor',
  CUSTOM_MARKERS = 'Custom Markers',
  INTERFACE_NAME = 'Interface Name',
  SENSOR_STATION_ERROR = 'Sensor Station Error',
  SENSOR_STATION_NORMAL = 'Sensor Station Normal',
  SENSOR_STATION_NOT_REACHABLE = 'Sensor Station Not Reachable',
  VALVE_ON = 'Valve ON',
  VALVE_OFF = 'Valve OFF',
  VALVE_ERROR = 'Valve ERROR',
  VALVE_WAITING = 'Valve WAITING',
  DEFAULT_ICON = 'Custom Markers',
  VALVE_OFF_LOADING = 'Valve OFF Loading',
}

export const ELegendIcon = {
  [ELegendName.CONTROLLERS]: CONTROLLERS_C,
  [ELegendName.WEATHER_STATIONS]: WEATHER_STATIONS,
  [ELegendName.SOIL_MOISTURE_SENSOR]: SOIL_MOISTURE_SENSOR,
  [ELegendName.FLOW_SENSOR]: FLOW_SENSOR,
  [ELegendName.CUSTOM_MARKERS]: CUSTOM_MARKERS,
  [ELegendName.INTERFACE_NAME]: INTERFACE_NAME,
  [ELegendName.SENSOR_STATION_ERROR]: SENSOR_STATION_ERROR,
  [ELegendName.SENSOR_STATION_NORMAL]: SENSOR_STATION_NORMAL,
  [ELegendName.SENSOR_STATION_NOT_REACHABLE]: SENSOR_STATION_NOT_REACHABLE,
  [ELegendName.VALVE_ON]: VALVE_ON,
  [ELegendName.VALVE_OFF]: VALVE_OFF,
  [ELegendName.VALVE_ERROR]: VALVE_ERROR,
  [ELegendName.VALVE_WAITING]: VALVE_WAITING,
  [ELegendName.VALVE_OFF_LOADING]: VALVE_OFF_LOADING,
}

interface ILegendSVG {
  name: ELegendName
  alt?: string
  className?: string
}

const getFormattedFileName = (legendName: ELegendName): string => {
  return legendName.toLowerCase().replace(/\s+/g, '_') + '.svg'
}

export const LegendSVGLoader = ({
  name,
  alt = 'SVG Icon',
  className = '',
}: ILegendSVG) => {
  const formattedName = getFormattedFileName(name)
  try {
    const SvgIcon = ELegendIcon[name]
    return <img src={SvgIcon} alt={alt} className={className} />
  } catch (error) {
    console.error(`Error loading SVG: ${formattedName}`, error)
    return <span>&nbsp;</span>
  }
}

interface LegendIconByNameProps {
  name: ELegendName
}

export const LegendIconByName: React.FC<LegendIconByNameProps> = ({ name }) => {
  if (!Object.values(ELegendName).includes(name as ELegendName)) {
    return <span></span>
  }
  return <LegendSVGLoader name={name} />
}

export const getStationMarkerIcon = (marker: MARKER) => {
  const urlIcon =
    ELegendIcon[marker.name as ELegendName] ||
    ELegendIcon[ELegendName.VALVE_OFF]
  return marker.id === MARKER_TYPE.SRC_Controller
    ? CONTROLLER_MARKER_ICON
    : urlIcon
}

// The SignalR StationUpdate now sends the 0-based StationState (`status`)
// instead of the full marker. Map it to the station's valve marker so the map
// icon can be resolved locally. Marker ids match what the backend previously
// sent (Valve ON/OFF/ERROR = 3/4/5, Valve WAITING = 15).
const VALVE_WAITING_MARKER_ID = 15
const STATION_STATUS_TO_MARKER: Record<
  number,
  { id: number; name: ELegendName }
> = {
  0: { id: MARKER_TYPE.Valve_OFF, name: ELegendName.VALVE_OFF }, // Inactive
  1: { id: MARKER_TYPE.Valve_OFF, name: ELegendName.VALVE_OFF }, // Idling
  2: { id: VALVE_WAITING_MARKER_ID, name: ELegendName.VALVE_WAITING }, // WaitSchedule
  3: { id: VALVE_WAITING_MARKER_ID, name: ELegendName.VALVE_WAITING }, // WaitStart
  4: { id: MARKER_TYPE.Valve_ON, name: ELegendName.VALVE_ON }, // Started
  5: { id: VALVE_WAITING_MARKER_ID, name: ELegendName.VALVE_WAITING }, // WaitStop
  6: { id: VALVE_WAITING_MARKER_ID, name: ELegendName.VALVE_WAITING }, // Soaking
  7: { id: MARKER_TYPE.Valve_ERROR, name: ELegendName.VALVE_ERROR }, // Error
  8: { id: VALVE_WAITING_MARKER_ID, name: ELegendName.VALVE_WAITING }, // RainSoaking
}

export const getStationMarkerByStatus = (status: number): MARKER => {
  const m =
    STATION_STATUS_TO_MARKER[status] ??
    STATION_STATUS_TO_MARKER[1] /* default OFF */
  return { id: m.id, name: m.name, imageUrl: '', s3Url: '' }
}
// Group 1: SRC Controller, Valve ON/OFF/ERROR/WAITING
// Group 2: Weather Stations, Soil Moisture Sensor, Flow Sensor
// Group 3: Custom Markers
// Group 4: Sensor Station Normal/Error/Not Reachable
export const MARKER_DISPLAY_ORDER = [1, 3, 4, 5, 15, 11, 6, 7, 9, 13, 12, 14]

export const MARKER_ID_TO_LEGEND: Record<number, ELegendName> = {
  1: ELegendName.CONTROLLERS,
  3: ELegendName.VALVE_ON,
  4: ELegendName.VALVE_OFF,
  5: ELegendName.VALVE_ERROR,
  6: ELegendName.SOIL_MOISTURE_SENSOR,
  7: ELegendName.FLOW_SENSOR,
  9: ELegendName.CUSTOM_MARKERS,
  10: ELegendName.INTERFACE_NAME,
  11: ELegendName.WEATHER_STATIONS,
  12: ELegendName.SENSOR_STATION_ERROR,
  13: ELegendName.SENSOR_STATION_NORMAL,
  14: ELegendName.SENSOR_STATION_NOT_REACHABLE,
  15: ELegendName.VALVE_WAITING,
}

// Telemetry sensor marker types shown in the Legend's dynamic "Sensors" group -
export const SENSOR_TYPE_DISPLAY_ORDER = [11, 6, 7, 16, 13]

// IDs that have a bottom border separator after them (marks the end of each group)
export const MARKER_BORDER_AFTER_IDS = new Set([15, 7, 9])

// IDs whose icons are wider and need -5px left margin adjustment
export const MARKER_WIDE_ICON_IDS = new Set([12, 13, 14])

// IDs that span the full row width instead of sharing the row with another item
export const MARKER_FULL_ROW_IDS = new Set([1])
